<header class="bar">
    <a class="home-link" [routerLink]="['/']" aria-label="Quipex home">
        <qpx-logo variant="white" size="sm" />
    </a>

    <div class="selectors">
        <button
            qpxButton
            matButton
            context="navigation"
            class="selector-trigger building-trigger pl-0!"
            type="button"
            aria-haspopup="dialog"
            [disabled]="buildingSelectorLoading()"
            [attr.aria-label]="buildingSelectorLabel()"
            [attr.aria-busy]="buildingSelectorLoading() || null"
            (click)="requestFindBuilding($event)"
        >
            <span class="building-identity">
                @if (buildingSelectorLoading()) {
                    <qpx-skeleton
                        class="selector-avatar-skeleton"
                        context="navigation"
                        width="2.5rem"
                        height="2.5rem"
                        borderRadius="var(--qpx-radius-sm)"
                    />
                } @else {
                    <qpx-avatar
                        fallbackIcon="lucideBuilding2"
                        colorContext="primary"
                        tone="muted"
                    />
                }
                <span class="trigger-copy">
                    @if (buildingSelectorLoading()) {
                        <qpx-skeleton
                            context="navigation"
                            width="6rem"
                            height="0.75rem"
                        />
                        <qpx-skeleton
                            context="navigation"
                            width="4.5rem"
                            height="0.625rem"
                        />
                    } @else {
                        @if (activeScope()?.currentBuilding; as building) {
                            <span class="trigger-primary">
                                {{ building | qpxBuildingName }}
                            </span>
                            <span class="trigger-secondary">
                                {{ building.address | qpxAddress: 'city' }}
                            </span>
                        } @else {
                            <span class="trigger-primary">
                                {{ buildingFallbackLabel() }}
                            </span>
                            <span class="trigger-secondary">
                                Choose a building
                            </span>
                        }
                    }
                </span>
            </span>
            <ng-icon
                qpx
                name="lucideChevronDown"
                matButtonIcon
                iconPositionEnd
            />
        </button>

        <button
            #moduleOrigin="cdkOverlayOrigin"
            cdkOverlayOrigin
            qpxButton
            matButton
            context="navigation"
            [class]="`selector-trigger module-trigger ${moduleClassName() ?? ''}`"
            type="button"
            aria-controls="app-top-bar-panel"
            [disabled]="moduleSelectorLoading()"
            [attr.aria-label]="moduleSelectorLabel()"
            [attr.aria-busy]="moduleSelectorLoading() || null"
            [attr.aria-expanded]="modulePanelOpen()"
            (click)="toggleModulePanel(moduleOrigin)"
        >
            @if (moduleSelectorLoading()) {
                <qpx-skeleton
                    matButtonIcon
                    class="mat-icon selector-icon-skeleton"
                    context="navigation"
                    width="1.25rem"
                    height="1.25rem"
                    borderRadius="var(--qpx-radius-xs)"
                />
            } @else {
                <ng-icon qpx [name]="moduleIcon()" matButtonIcon />
            }
            <span class="trigger-label">
                @if (moduleSelectorLoading()) {
                    <qpx-skeleton
                        context="navigation"
                        width="4.5rem"
                        height="0.75rem"
                    />
                } @else {
                    {{ moduleLabel() }}
                }
            </span>
            <ng-icon
                qpx
                name="lucideChevronDown"
                matButtonIcon
                iconPositionEnd
            />
        </button>
    </div>

    <div class="actions">
        @if (inAppHelpSupportAvailable) {
            <button
                qpxButton
                matIconButton
                context="navigation"
                class="utility-action icon-only"
                type="button"
                [attr.aria-label]="
                    hasUnreadReleaseNotes()
                        ? 'What\'s new, unread update'
                        : 'What\'s new'
                "
                [qpxTooltip]="
                    hasUnreadReleaseNotes()
                        ? 'What\'s new — unread update'
                        : 'What\'s new'
                "
                (click)="requestWhatsNew()"
            >
                <ng-icon
                    qpx
                    name="lucideMegaphone"
                    matBadge="●"
                    matBadgeSize="small"
                    [matBadgeHidden]="!hasUnreadReleaseNotes()"
                />
            </button>
        }

        @if (contactUsHref; as href) {
            <a
                qpxButton
                matIconButton
                context="navigation"
                class="utility-action icon-only"
                [href]="href"
                aria-label="Help and contact"
                qpxTooltip="Help and contact"
            >
                <ng-icon qpx name="lucideCircleHelp" />
            </a>
        }

        <button
            qpxButton
            matButton
            context="navigation"
            class="user-trigger pl-0!"
            type="button"
            [attr.aria-label]="userMenuLabel()"
            [matMenuTriggerFor]="userMenu"
            (click)="closeForUserMenu()"
        >
            <span class="user-identity">
                <qpx-avatar
                    [colorKey]="me().id"
                    [initials]="userInitials()"
                    [name]="me().displayName"
                />
                <span class="user-copy">
                    <span class="user-name">{{ me().displayName }}</span>
                    <span class="company-name">{{ org().name }}</span>
                </span>
            </span>
            <ng-icon
                qpx
                name="lucideChevronDown"
                matButtonIcon
                iconPositionEnd
            />
        </button>
    </div>

    <span
        #panelOrigin="cdkOverlayOrigin"
        cdkOverlayOrigin
        class="panel-origin"
        aria-hidden="true"
    ></span>
</header>

<mat-menu #userMenu="matMenu" class="app-top-bar-user-menu">
    @if (manageCompanyShortcut(); as shortcut) {
        <a mat-menu-item [routerLink]="shortcut.route">
            <ng-icon qpx [name]="shortcut.icon" matMenuItemIcon />
            <span>{{ shortcut.label }}</span>
        </a>
    }

    <button mat-menu-item type="button" (click)="signOut()">
        <ng-icon qpx name="lucideLogOut" matMenuItemIcon />
        <span>Sign out</span>
    </button>
</mat-menu>

<ng-template
    cdkConnectedOverlay
    #connectedOverlay="cdkConnectedOverlay"
    [cdkConnectedOverlayOrigin]="panelOrigin"
    [cdkConnectedOverlayOpen]="activePanel() !== null"
    [cdkConnectedOverlayPositions]="panelPositions"
    [cdkConnectedOverlayPush]="true"
    [cdkConnectedOverlayFlexibleDimensions]="true"
    [cdkConnectedOverlayGrowAfterOpen]="true"
    [cdkConnectedOverlayViewportMargin]="0"
    [cdkConnectedOverlayDisposeOnNavigation]="false"
    (attach)="onOverlayAttached()"
    (detach)="onOverlayDetached()"
    (overlayOutsideClick)="closeForOutsideClick()"
>
    @if (activePanel(); as panel) {
        <section
            #panel
            id="app-top-bar-panel"
            class="panel"
            data-testid="app-top-bar-panel"
            tabindex="-1"
            (keydown)="onPanelKeydown($event)"
            (focusout)="onPanelFocusOut($event)"
        >
            @if (activeScope(); as scope) {
                @if (moduleMenuContext(); as context) {
                    <ng-container
                        *ngTemplateOutlet="scope.moduleMenu; context: context"
                    />
                }
            }
        </section>
    }
</ng-template>
